/**
 * Recurring Tickets — TanStack Query hooks.
 *
 * React Query v4 API (`useMutation(fn, { onSuccess })`, `useQuery({ queryKey, queryFn })`).
 *
 * Rules:
 *  - NO toasts in this file. Toasts live in the UI/component layer only
 *    (per user memory `feedback_toast_placement.md`). Mutation consumers receive the raw
 *    success/error result and fire toasts there.
 *  - All query keys go through the central `queryKeys.recurringTickets` factory.
 *  - List hook uses `select` to normalise the non-standard envelope via `unwrapList<T>()`
 *    so consumers read `{ items, totalCount, totalPages, length }` directly.
 *  - Pause/resume mutations do NOT swallow errors — the thrown error bubbles to the caller
 *    with `.message` populated, so the UI can run `matchPauseResumeConflict(error)` to
 *    decide between "Already paused" / "Already active" / generic error copy (BUG-11).
 *  - Delete uses `setQueriesData` with the `recurringTickets.all()` prefix for optimistic
 *    removal, with rollback context on error.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";
import {
  bulkDeleteRecurringTickets,
  bulkPauseRecurringTickets,
  bulkResumeRecurringTickets,
  bulkSkipNextCycleRecurringTickets,
  createRecurringTicket,
  createRecurringTicketNow,
  deleteRecurringTicket,
  getRecurringTicketAuditTrail,
  getRecurringTicketById,
  getRecurringTicketsList,
  pauseRecurringTicket,
  resumeRecurringTicket,
  skipRecurringTicketDates,
  unwrapList,
  updateRecurringTicket,
} from "controller/api";
import { queryKeys } from "controller/cache";
import type {
  BulkDeleteRecurringTicketsResponse,
  BulkPauseRecurringTicketsResponse,
  BulkResumeRecurringTicketsResponse,
  BulkSkipNextCycleRecurringTicketsResponse,
  CreateRecurringTicketNowResponse,
  CreateRecurringTicketPayload,
  CreateRecurringTicketResponse,
  DeleteRecurringTicketResponse,
  GetRecurringTicketAuditTrailResponse,
  GetRecurringTicketByIdResponse,
  GetRecurringTicketsQuery,
  ListRecurringTicketsResponse,
  PauseRecurringTicketPayload,
  PauseRecurringTicketResponse,
  RecurringTicketListEnvelope,
  RecurringTicketListItem,
  ResumeRecurringTicketResponse,
  SkipRecurringTicketDatesPayload,
  SkipRecurringTicketDatesResponse,
  UpdateRecurringTicketPayload,
  UpdateRecurringTicketResponse,
} from "shared";

// ----------------------------------------------------------------------------
// Normalised list shape consumed by components
// ----------------------------------------------------------------------------

export interface RecurringTicketsListData {
  items: RecurringTicketListItem[];
  totalCount: number;
  totalPages: number;
  length: number;
}

// ----------------------------------------------------------------------------
// GET list
// ----------------------------------------------------------------------------

export const useGetRecurringTicketsList = (
  params: GetRecurringTicketsQuery,
  options?: {
    enabled?: boolean;
  }
) => {
  // Split filters from pagination/sort so the query key matches the factory signature
  // (filters bucket vs params bucket). This is cosmetic but keeps cache keys stable.
  const {
    page,
    per_page,
    searchQuery,
    sortColumn,
    sortDirection,
    ...filters
  } = params;

  return useQuery<
    ListRecurringTicketsResponse,
    unknown,
    RecurringTicketsListData
  >({
    queryKey: queryKeys.recurringTickets.list(filters, {
      page,
      per_page,
      searchQuery,
      sortColumn,
      sortDirection,
    }),
    queryFn: () => getRecurringTicketsList(params),
    // `unwrapList` is cheap (object spread only) — safe to run on every render.
    select: (envelope) => unwrapList<RecurringTicketListItem>(envelope),
    enabled: options?.enabled ?? true,
    keepPreviousData: true,
  });
};

// ----------------------------------------------------------------------------
// GET by id
// ----------------------------------------------------------------------------

export const useGetRecurringTicketById = (
  id: string | undefined,
  options?: Omit<
    UseQueryOptions<GetRecurringTicketByIdResponse, unknown>,
    "queryKey" | "queryFn" | "enabled"
  > & { enabled?: boolean }
) => {
  const enabled = !!id && options?.enabled !== false;
  return useQuery<GetRecurringTicketByIdResponse, unknown>({
    queryKey: queryKeys.recurringTickets.detail(id ?? "__disabled__"),
    queryFn: () => getRecurringTicketById(id as string),
    enabled,
    ...options,
  });
};

// ----------------------------------------------------------------------------
// POST create
// ----------------------------------------------------------------------------

export const useCreateRecurringTicket = (
  options?: UseMutationOptions<
    CreateRecurringTicketResponse,
    unknown,
    CreateRecurringTicketPayload
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    CreateRecurringTicketResponse,
    unknown,
    CreateRecurringTicketPayload
  >(createRecurringTicket, {
    ...options,
    onSuccess: (data, variables, context) => {
      // Invalidate all list queries regardless of filter shape.
      queryClient.invalidateQueries(queryKeys.recurringTickets.all());

      // BUG-05: create response omits checklists/tags/attachments/assignees/schedulings.
      // If the caller needs those fields, we prime the detail cache with a follow-up refetch.
      const mappingId = data?.data?.mapping_id;
      if (mappingId) {
        queryClient.invalidateQueries(
          queryKeys.recurringTickets.detail(mappingId)
        );
      }

      options?.onSuccess?.(data, variables, context);
    },
  });
};

// ----------------------------------------------------------------------------
// PATCH update
// ----------------------------------------------------------------------------

export const useUpdateRecurringTicket = (
  options?: UseMutationOptions<
    UpdateRecurringTicketResponse,
    unknown,
    { mappingId: string; payload: UpdateRecurringTicketPayload }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateRecurringTicketResponse,
    unknown,
    { mappingId: string; payload: UpdateRecurringTicketPayload }
  >(({ mappingId, payload }) => updateRecurringTicket(mappingId, payload), {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(queryKeys.recurringTickets.all());
      queryClient.invalidateQueries(
        queryKeys.recurringTickets.detail(variables.mappingId)
      );
      options?.onSuccess?.(data, variables, context);
    },
  });
};

// ----------------------------------------------------------------------------
// PATCH pause
// ----------------------------------------------------------------------------

/**
 * BUG-11: conflict errors (already paused) are thrown with `.message` populated.
 * The caller is expected to run `matchPauseConflict(error)` (from `controller/api`) in
 * its own `onError`. We do NOT swallow or classify the error here.
 */
export const usePauseRecurringTicket = (
  options?: UseMutationOptions<
    PauseRecurringTicketResponse,
    unknown,
    { mappingId: string; body?: PauseRecurringTicketPayload }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    PauseRecurringTicketResponse,
    unknown,
    { mappingId: string; body?: PauseRecurringTicketPayload }
  >(({ mappingId, body }) => pauseRecurringTicket(mappingId, body), {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(queryKeys.recurringTickets.all());
      queryClient.invalidateQueries(
        queryKeys.recurringTickets.detail(variables.mappingId)
      );
      options?.onSuccess?.(data, variables, context);
    },
  });
};

// ----------------------------------------------------------------------------
// PATCH resume
// ----------------------------------------------------------------------------

export const useResumeRecurringTicket = (
  options?: UseMutationOptions<
    ResumeRecurringTicketResponse,
    unknown,
    { mappingId: string }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    ResumeRecurringTicketResponse,
    unknown,
    { mappingId: string }
  >(({ mappingId }) => resumeRecurringTicket(mappingId), {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(queryKeys.recurringTickets.all());
      queryClient.invalidateQueries(
        queryKeys.recurringTickets.detail(variables.mappingId)
      );
      options?.onSuccess?.(data, variables, context);
    },
  });
};

// ----------------------------------------------------------------------------
// DELETE (soft-delete) — with optimistic cache removal + rollback
// ----------------------------------------------------------------------------

interface DeleteMutationContext {
  /** Snapshot of all list queries keyed by the cached queryKey. */
  previous: Array<{ key: readonly unknown[]; data: unknown }>;
}

export const useDeleteRecurringTicket = (
  options?: UseMutationOptions<
    DeleteRecurringTicketResponse,
    unknown,
    { mappingId: string },
    DeleteMutationContext
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    DeleteRecurringTicketResponse,
    unknown,
    { mappingId: string },
    DeleteMutationContext
  >(({ mappingId }) => deleteRecurringTicket(mappingId), {
    ...options,
    onMutate: async (variables) => {
      // Cancel in-flight list refetches so they don't overwrite our optimistic write.
      await queryClient.cancelQueries(queryKeys.recurringTickets.all());

      // Snapshot every matching list query so we can roll back on error.
      const snapshots = queryClient.getQueriesData<ListRecurringTicketsResponse>(
        queryKeys.recurringTickets.all()
      );

      const previous: DeleteMutationContext["previous"] = snapshots.map(
        ([key, data]) => ({ key, data })
      );

      // Optimistically remove the row from every matching list cache.
      queryClient.setQueriesData<ListRecurringTicketsResponse | undefined>(
        queryKeys.recurringTickets.all(),
        (current) => {
          if (!current?.data?.result) return current;
          const envelope: RecurringTicketListEnvelope<RecurringTicketListItem> =
            current.data;
          const nextResult = envelope.result.filter(
            (row) => row.mapping_id !== variables.mappingId
          );
          const removed = nextResult.length !== envelope.result.length;
          if (!removed) return current;
          return {
            ...current,
            data: {
              ...envelope,
              result: nextResult,
              length: Math.max(0, envelope.length - 1),
              totalCount: Math.max(0, envelope.totalCount - 1),
            },
          };
        }
      );

      return { previous };
    },
    onError: (error, variables, context) => {
      // Roll back every snapshot.
      if (context?.previous) {
        for (const snapshot of context.previous) {
          queryClient.setQueryData(snapshot.key, snapshot.data);
        }
      }
      options?.onError?.(error, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      // Always re-sync with the server after settle.
      queryClient.invalidateQueries(queryKeys.recurringTickets.all());
      options?.onSettled?.(data, error, variables, context);
    },
  });
};

// ----------------------------------------------------------------------------
// PATCH bulk pause
// ----------------------------------------------------------------------------

export const useBulkPauseRecurringTickets = (
  options?: UseMutationOptions<
    BulkPauseRecurringTicketsResponse,
    unknown,
    { recurringTicketIds: string[] }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    BulkPauseRecurringTicketsResponse,
    unknown,
    { recurringTicketIds: string[] }
  >(
    ({ recurringTicketIds }) => bulkPauseRecurringTickets(recurringTicketIds),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(queryKeys.recurringTickets.all());
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};

// ----------------------------------------------------------------------------
// PATCH bulk resume
// ----------------------------------------------------------------------------

export const useBulkResumeRecurringTickets = (
  options?: UseMutationOptions<
    BulkResumeRecurringTicketsResponse,
    unknown,
    { recurringTicketIds: string[] }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    BulkResumeRecurringTicketsResponse,
    unknown,
    { recurringTicketIds: string[] }
  >(
    ({ recurringTicketIds }) => bulkResumeRecurringTickets(recurringTicketIds),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(queryKeys.recurringTickets.all());
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};

// ----------------------------------------------------------------------------
// PATCH bulk skip-next-cycle
// ----------------------------------------------------------------------------

export const useBulkSkipNextCycleRecurringTickets = (
  options?: UseMutationOptions<
    BulkSkipNextCycleRecurringTicketsResponse,
    unknown,
    { recurringTicketIds: string[] }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    BulkSkipNextCycleRecurringTicketsResponse,
    unknown,
    { recurringTicketIds: string[] }
  >(
    ({ recurringTicketIds }) =>
      bulkSkipNextCycleRecurringTickets(recurringTicketIds),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        // next_run changes → refresh list + any detail rows
        queryClient.invalidateQueries(queryKeys.recurringTickets.all());
        options?.onSuccess?.(data, variables, context);
      },
    },
  );
};

// ----------------------------------------------------------------------------
// POST skip-dates (skip specific scheduled dates)
// ----------------------------------------------------------------------------

export const useSkipRecurringTicketDates = (
  options?: UseMutationOptions<
    SkipRecurringTicketDatesResponse,
    unknown,
    { recurringTicketId: string; payload: SkipRecurringTicketDatesPayload }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    SkipRecurringTicketDatesResponse,
    unknown,
    { recurringTicketId: string; payload: SkipRecurringTicketDatesPayload }
  >(
    ({ recurringTicketId, payload }) =>
      skipRecurringTicketDates(recurringTicketId, payload),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(queryKeys.recurringTickets.all());
        queryClient.invalidateQueries(["home_screen", "upcoming_activities"]);
        // Home v2 calendar — the skipped occurrence has to drop out of the week.
        queryClient.invalidateQueries(["home_calendar"]);
        options?.onSuccess?.(data, variables, context);
      },
    },
  );
};

// ----------------------------------------------------------------------------
// POST bulk delete
// ----------------------------------------------------------------------------

export const useBulkDeleteRecurringTickets = (
  options?: UseMutationOptions<
    BulkDeleteRecurringTicketsResponse,
    unknown,
    { recurringTicketIds: string[] }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    BulkDeleteRecurringTicketsResponse,
    unknown,
    { recurringTicketIds: string[] }
  >(
    ({ recurringTicketIds }) => bulkDeleteRecurringTickets(recurringTicketIds),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(queryKeys.recurringTickets.all());
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};

// ----------------------------------------------------------------------------
// POST create-now (manual ticket generation)
// ----------------------------------------------------------------------------

export const useCreateRecurringTicketNow = (
  options?: UseMutationOptions<
    CreateRecurringTicketNowResponse,
    unknown,
    { mappingId: string }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    CreateRecurringTicketNowResponse,
    unknown,
    { mappingId: string }
  >(({ mappingId }) => createRecurringTicketNow(mappingId), {
    ...options,
    onSuccess: (data, variables, context) => {
      // Refresh the list + the detail + audit trail since a new ticket was generated.
      queryClient.invalidateQueries(queryKeys.recurringTickets.all());
      queryClient.invalidateQueries(
        queryKeys.recurringTickets.detail(variables.mappingId),
      );
      queryClient.invalidateQueries(
        queryKeys.recurringTickets.auditTrail(variables.mappingId),
      );
      // Home screens list the upcoming occurrence — it is now a real ticket.
      queryClient.invalidateQueries(["home_screen", "upcoming_activities"]);
      queryClient.invalidateQueries(["home_calendar"]);
      options?.onSuccess?.(data, variables, context);
    },
  });
};

// ----------------------------------------------------------------------------
// Audit trail
// ----------------------------------------------------------------------------

export const useGetRecurringTicketAuditTrail = (
  mappingId: string | undefined | null,
  options?: Omit<
    UseQueryOptions<GetRecurringTicketAuditTrailResponse>,
    "queryKey" | "queryFn" | "enabled"
  >
) => {
  return useQuery<GetRecurringTicketAuditTrailResponse>({
    queryKey: queryKeys.recurringTickets.auditTrail(mappingId ?? "__disabled__"),
    queryFn: () => getRecurringTicketAuditTrail(mappingId as string),
    enabled: !!mappingId,
    ...options,
  });
};
